import { useFormConfigStore } from '../state/store';
import { classifyField } from '@capture/shared';
import { useFieldValidator } from '../hooks/useFieldValidator';
import './BatchValidationSummary.css';

/**
 * Summary card under the Header tab. Shows (a) how many header fields
 * are marked `required: true` — the publish-gate for incoming batches —
 * and (b) any `header.*` publish-gate validator errors/warnings that
 * `useFieldValidator` surfaces (wired via `registerHeaderValidators`
 * in the API + `validateContent` in the shared package).
 */
export function BatchValidationSummary() {
  const content = useFormConfigStore((s) => s.content);
  const { errors } = useFieldValidator();
  if (!content) return null;

  const requiredCount = content.fields.filter((f) => {
    if (classifyField(f) !== 'header') return false;
    const first = f.stageMap.import?.[0];
    if (!first || first.kind !== 'populate') return false;
    const src = first.source;
    if (src.type !== 'header-xpath' && src.type !== 'header-path') return false;
    return Boolean((src as { required?: boolean }).required);
  }).length;

  // Header-scoped publish-gate messages — surfaced whether they were
  // bucketed by fieldName, behaviorId, or path.join('.').
  const headerMessages = Array.from(errors.values())
    .flat()
    .filter((e) => e.code.startsWith('header.'));

  return (
    <section className="batch-validation-summary">
      <h3 className="batch-validation-summary__title">Batch validation rules</h3>
      <p className="batch-validation-summary__text">
        {requiredCount} required header field{requiredCount === 1 ? '' : 's'}.
        Missing values will reject incoming batches into{' '}
        <code className="batch-validation-summary__code">needs-header-review</code>
        {' '}for operator triage.
      </p>
      {headerMessages.length > 0 && (
        <ul className="batch-validation-summary__messages" role="alert">
          {headerMessages.map((m, i) => (
            <li
              key={`${m.code}-${i}`}
              className={`batch-validation-summary__msg batch-validation-summary__msg--${m.severity}`}
            >
              <code>{m.code}</code> {m.message}
              {m.fieldName && (
                <span className="batch-validation-summary__field"> · {m.fieldName}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="batch-validation-summary__footnote">
        Per-field validation rules are configured in the Rules tab.
      </p>
    </section>
  );
}
